import { useMemo, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, CalendarDays } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useUser } from '@/shared/hooks/useAuth';
import { useMeetings, useMeetingMutations } from '../hooks/useMeetings';
import { MeetingsFilterBar } from '../components/MeetingsFilterBar';
import { MeetingCard } from '../components/MeetingCard';
import { MeetingFormModal } from '../components/MeetingFormModal';
import { MeetingDrawer } from '../components/MeetingDrawer';
import type { Meeting, CreateMeetingPayload, UpdateMeetingPayload } from '../types';

export function MeetingsPage() {
  const user = useUser();
  const role = user?.role ?? '';
  const isPrivileged = ['admin', 'superadmin'].includes(role);

  // Authorization mirrors the backend: superadmin = any; admin = dept-scoped
  // (the list is already filtered to their dept); else organizer only.
  const canModify = useCallback(
    (meeting: Meeting) => isPrivileged || meeting.organizer_id === user?.id,
    [isPrivileged, user?.id],
  );

  // ─── Filters ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const filters = useMemo(() => (status ? { status } : {}), [status]);

  // ─── Data ─────────────────────────────────────────────────────────────────
  const { data: meetings = [], isLoading, isError } = useMeetings(filters);
  const { createMeeting, updateMeeting, deleteMeeting } = useMeetingMutations();

  // ─── UI state ───────────────────────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [editMeeting, setEditMeeting] = useState<Meeting | null>(null);
  const [drawerMeeting, setDrawerMeeting] = useState<Meeting | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Meeting | null>(null);

  // ─── Client-side search on title ─────────────────────────────────────────────
  const visibleMeetings = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return meetings;
    return meetings.filter((m) => (m.title ?? '').toLowerCase().includes(q));
  }, [meetings, search]);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleCreate = () => {
    setEditMeeting(null);
    setFormOpen(true);
  };

  const handleEdit = useCallback((meeting: Meeting) => {
    setEditMeeting(meeting);
    setFormOpen(true);
  }, []);

  const handleCreateSubmit = (payload: CreateMeetingPayload) => {
    createMeeting.mutate(payload, {
      onSuccess: () => {
        toast.success('Meeting created.');
        setFormOpen(false);
      },
      onError: () => toast.error('Failed to create meeting.'),
    });
  };

  const handleUpdateSubmit = (id: string, payload: UpdateMeetingPayload) => {
    updateMeeting.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast.success('Meeting updated.');
          setFormOpen(false);
          setEditMeeting(null);
          setDrawerMeeting(null);
        },
        onError: () => toast.error('Failed to update meeting.'),
      },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMeeting.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Meeting deleted.');
        setDeleteTarget(null);
      },
      onError: () => toast.error('Failed to delete meeting.'),
    });
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col gap-4 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarDays className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">Meetings</h1>
            <p className="text-body-sm text-on-surface-variant">
              {visibleMeetings.length} meeting{visibleMeetings.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        {/* Any authenticated user may create (mirrors backend). */}
        <Button onClick={handleCreate} size="md">
          <Plus className="h-4 w-4" />
          New Meeting
        </Button>
      </div>

      {/* Filters */}
      <MeetingsFilterBar
        search={search}
        status={status}
        onSearchChange={setSearch}
        onStatusChange={setStatus}
      />

      {/* Grid */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState
          title="Failed to load meetings"
          description="Check your connection or try refreshing."
        />
      ) : visibleMeetings.length === 0 ? (
        <EmptyState
          title={search ? 'No matching meetings' : 'No meetings scheduled'}
          description={search ? 'Try a different search term.' : 'Create your first meeting to get started.'}
          action={!search ? <Button onClick={handleCreate}>New Meeting</Button> : undefined}
        />
      ) : (
        <div className="grid flex-1 auto-rows-min grid-cols-1 gap-4 overflow-y-auto md:grid-cols-2 xl:grid-cols-3">
          {visibleMeetings.map((meeting) => (
            <MeetingCard
              key={meeting.id}
              meeting={meeting}
              canEdit={canModify(meeting)}
              onOpen={setDrawerMeeting}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <MeetingFormModal
        open={formOpen}
        meeting={editMeeting}
        loading={createMeeting.isPending || updateMeeting.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditMeeting(null);
        }}
        onCreate={handleCreateSubmit}
        onUpdate={handleUpdateSubmit}
      />

      {/* Detail Drawer */}
      <MeetingDrawer
        meeting={drawerMeeting}
        canModify={drawerMeeting ? canModify(drawerMeeting) : false}
        saving={updateMeeting.isPending}
        onClose={() => setDrawerMeeting(null)}
        onSave={handleUpdateSubmit}
        onDelete={(meeting) => {
          setDrawerMeeting(null);
          setDeleteTarget(meeting);
        }}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Meeting"
        message={`Delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteMeeting.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
